import Spline from '@splinetool/react-spline';
import { FilePlusIcon } from 'lucide-react';
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useState
} from 'react';

import PromptField from './PromptField';
import QuestionAnswer from './QuestionAnswer';

interface Props {
  businessEntities: BusinessEntity[];
  handlePresetSelectRef: RefObject<((label: string) => void) | null>;
  isFetchingChatData: boolean;
  pending: boolean;
  presetPrompts: PresetPrompt[];
  promptsAndAnswers: PromptAndAnswer[];
  ref: RefObject<HTMLDivElement | null>;
  sessionId: string;
  setPending: Dispatch<SetStateAction<boolean>>;
  setPromptsAndAnswers: Dispatch<SetStateAction<PromptAndAnswer[]>>;
  socket: null | WebSocket;
  userId: string;
}

const ChatPanel = ({
  businessEntities,
  handlePresetSelectRef,
  isFetchingChatData,
  pending,
  presetPrompts,
  promptsAndAnswers,
  ref,
  sessionId,
  setPending,
  setPromptsAndAnswers,
  socket,
  userId
}: Props) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only hide if we're leaving the container (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Find the PromptField's file input and trigger it
      // We'll search for it in the DOM since we can't easily pass a ref
      const fileInput = document.querySelector<HTMLInputElement>(
        'input[type="file"][accept*=".txt"]'
      );
      if (fileInput) {
        // Create a DataTransfer object and assign files to the input
        const dataTransfer = new DataTransfer();
        Array.from(e.dataTransfer.files).forEach(file => {
          // Validate file type
          const extension = file.name.split('.').pop()?.toLowerCase();
          const allowedExtensions = [
            '.txt',
            '.md',
            '.pdf',
            '.docx',
            '.csv',
            '.json'
          ];
          if (extension && allowedExtensions.includes(`.${extension}`)) {
            dataTransfer.items.add(file);
          }
        });

        if (dataTransfer.files.length > 0) {
          fileInput.files = dataTransfer.files;
          // Trigger change event
          const changeEvent = new Event('change', { bubbles: true });
          fileInput.dispatchEvent(changeEvent);
        }
      }
    }
  }, []);

  if (isFetchingChatData) {
    return (
      <div className="flex grow items-center justify-center px-3">
        Loading...
      </div>
    );
  }

  if (promptsAndAnswers.length === 0) {
    return (
      <div
        className="flex grow items-center justify-center px-3"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDraggingOver && (
          <div className="bg-background/80 pointer-events-none fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
            <div className="text-brand border-primary bg-background flex items-center gap-x-2 rounded-lg border-2 border-dashed p-8">
              <FilePlusIcon size={24} />
              <p className="text-lg font-medium">Drop files here to upload</p>
            </div>
          </div>
        )}
        <div className="-mt-60 w-[640px] max-w-full lg:w-[720px]">
          <div className="h-80 max-lg:-mx-3">
            <Spline scene="https://prod.spline.design/WfddZFvSUvUBcVMc/scene.splinecode" />
          </div>
          <PromptField
            businessEntities={businessEntities}
            handlePresetSelectRef={handlePresetSelectRef}
            key={sessionId} // This is to clear the prompt field when the user presses "New Chat".
            pending={pending}
            presetPrompts={presetPrompts}
            sessionId={sessionId}
            setPending={setPending}
            setPromptsAndAnswers={setPromptsAndAnswers}
            socket={socket}
            state="prompting"
            userId={userId}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col items-center gap-y-6 overflow-y-auto px-3 pt-3 pb-6 contain-paint"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDraggingOver && (
        <div className="bg-background/80 pointer-events-none fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="text-brand border-primary bg-background flex items-center gap-x-2 rounded-lg border-2 border-dashed p-8">
            <FilePlusIcon size={24} />
            <p className="text-lg font-medium">Drop files here to upload</p>
          </div>
        </div>
      )}
      {promptsAndAnswers.map((promptAndAnswer, index) => (
        <QuestionAnswer
          businessEntities={businessEntities}
          handlePresetSelectRef={handlePresetSelectRef}
          isLast={index === promptsAndAnswers.length - 1}
          key={promptAndAnswer.id}
          pending={pending}
          presetPrompts={presetPrompts}
          promptAndAnswer={promptAndAnswer}
          ref={ref}
          sessionId={sessionId}
          setPending={setPending}
          setPromptsAndAnswers={setPromptsAndAnswers}
          socket={socket}
          userId={userId}
        />
      ))}
      <div className="sticky bottom-8 mt-auto w-[740px] max-w-full lg:w-[820px]">
        <PromptField
          businessEntities={businessEntities}
          handlePresetSelectRef={handlePresetSelectRef}
          messageId={promptsAndAnswers[promptsAndAnswers.length - 1].id}
          pending={pending}
          presetPrompts={presetPrompts}
          sessionId={sessionId}
          setPending={setPending}
          setPromptsAndAnswers={setPromptsAndAnswers}
          socket={socket}
          state="prompting"
          userId={userId}
        />
        <div className="absolute h-8 w-full bg-white" />
      </div>
    </div>
  );
};

export default ChatPanel;
